/**
 * Tap/click toggle for the refer-a-friend card's Yotpo widget reveal.
 * Desktop mouse hover is handled entirely in CSS (see
 * rewards-refer-faq.liquid); this covers touch and keyboard.
 *
 * Unlike a simple "close on any non-toggle click" listener, a click
 * anywhere outside the card is what closes it — a click inside the card
 * (e.g. on the revealed Yotpo widget) is left alone, since the widget is
 * genuinely interactive and its own clicks need to reach it instead of
 * being swallowed by this toggle logic.
 */
(function () {
  function closeCard(card) {
    var toggle = card.querySelector("[data-refer-toggle]");
    card.classList.remove("is-revealed");
    if (toggle) toggle.setAttribute("aria-expanded", "false");
  }

  function openCard(card) {
    var toggle = card.querySelector("[data-refer-toggle]");
    card.classList.add("is-revealed");
    if (toggle) toggle.setAttribute("aria-expanded", "true");
  }

  document.addEventListener("click", function (event) {
    var toggle = event.target.closest("[data-refer-toggle]");

    if (toggle) {
      var card = toggle.closest("[data-refer-card]");
      if (!card) return;
      if (card.classList.contains("is-revealed")) {
        closeCard(card);
      } else {
        openCard(card);
      }
      return;
    }

    var withinCard = event.target.closest("[data-refer-card]");
    if (!withinCard) {
      document.querySelectorAll(".rewards-refer-faq__refer.is-revealed").forEach(closeCard);
    }
  });

  // On hover-capable devices, a click can leave a card open via JS after the
  // mouse moves off it — CSS :hover no longer applies but .is-revealed still
  // does, so it stays visually stuck open. Close it on mouseleave so click
  // and hover state never disagree.
  if (window.matchMedia("(hover: hover)").matches) {
    document.querySelectorAll("[data-refer-card]").forEach(function (card) {
      card.addEventListener("mouseleave", function () {
        closeCard(card);
      });
    });
  }
})();

/**
 * Locks the refer card's height to the FAQ column's own natural height —
 * measured with every accordion collapsed (their default state, since none
 * render with the `open` attribute) — so opening an accordion later makes
 * the FAQ column taller than the image instead of pulling the image along
 * with it. CSS alone can't do this: grid stretch always tracks the FAQ
 * column's CURRENT live height, and there's no way in CSS to say "match
 * this column's height, but only as it was before anything changed".
 *
 * Desktop only (matching the same 750px breakpoint the rest of this
 * section's CSS uses) — mobile stacks the columns, so there's no FAQ
 * height to match at all there; the inline height is cleared entirely
 * below that width so the aspect-ratio-driven mobile layout takes over
 * exactly as it did before this script existed.
 */
(function () {
  function syncHeight() {
    var faqColumn = document.querySelector(".rewards-refer-faq__faq");
    var referCard = document.querySelector(".rewards-refer-faq__refer");
    if (!faqColumn || !referCard) return;

    if (window.matchMedia("(min-width: 750px)").matches) {
      referCard.style.height = faqColumn.offsetHeight + "px";
    } else {
      referCard.style.height = "";
    }
  }

  // Wait for window load (not just DOMContentLoaded) so webfonts/images
  // that could still shift the FAQ column's text height have settled.
  window.addEventListener("load", syncHeight);

  // Re-measure on resize so switching between mobile/desktop layouts, or
  // resizing an open desktop window, reflects the current width — debounced
  // since resize fires continuously while dragging. This assumes every
  // accordion is collapsed at the moment of resize, same as on load; if one
  // happens to be open exactly when a resize settles, this will briefly
  // lock to that taller height until the next resize.
  var resizeTimer;
  window.addEventListener("resize", function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(syncHeight, 150);
  });
})();

/**
 * Scrolls to #refer-a-friend on a cold page load (e.g. a menu link
 * opened in a new tab). The browser's own native jump-to-fragment fires
 * before the sections above the card have finished loading and pushed it
 * down the page, landing far short — so this does the scroll itself
 * instead.
 *
 * The card sits directly below Rewards Birthday Input, whose Yotpo
 * widget mounts asynchronously and can reflow through several render
 * states (loading tile → logged-out tile → form, etc.) before settling —
 * each step nudges the page's height, and with it the card's position.
 *
 * Two things made earlier attempts inconsistent (sometimes one clean
 * jump, sometimes 2-3 corrections, sometimes what looked like a slow
 * continuous scroll): (1) an immediate scroll on load PLUS a later
 * debounced correction meant two separate `behavior: "smooth"` calls
 * could overlap — a second smooth scroll issued mid-animation redirects
 * the first rather than queuing after it, which looks like a stutter or
 * a slow crawl; (2) the browser's own native jump-to-fragment was still
 * live alongside both of those, an unpredictable third source of motion.
 * This version strips the hash up front so the native jump never fires,
 * and performs exactly one scroll, guarded by a `done` flag so it can't
 * run twice no matter which trigger reaches it first: the debounced
 * ResizeObserver (once the page actually goes quiet) or the timeout
 * fallback (in case nothing ever resizes).
 *
 * Earlier versions also cancelled entirely if the visitor scrolled or
 * touched before the correction fired — deliberately deferring to a
 * manual scroll. In practice the debounce/fallback window was long
 * enough that an impatient visitor scrolling on their own is the common
 * case, not an edge case, so cancelling left them stranded short of the
 * card more often than not. This version always corrects regardless of
 * interaction, and the window below is deliberately short (~1.8s worst
 * case) specifically to make that override brief and rare rather than a
 * visible fight with someone who scrolled away on purpose.
 */
(function () {
  if (window.location.hash !== "#refer-a-friend") return;

  var path = location.pathname + location.search;
  history.replaceState(null, "", path);

  var done = false;
  var debounceTimer, hardStop, observer;

  function scrollToCard() {
    if (done) return;
    done = true;
    clearTimeout(debounceTimer);
    clearTimeout(hardStop);
    observer.disconnect();

    var target = document.getElementById("refer-a-friend");
    if (!target) return;
    window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY, behavior: "smooth" });
    // Restore the hash (without re-triggering a native scroll) so the URL
    // still reflects the anchor for sharing/bookmarking.
    history.replaceState(null, "", path + "#refer-a-friend");
  }

  window.addEventListener("load", function () {
    observer = new ResizeObserver(function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(scrollToCard, 400);
    });
    observer.observe(document.body);

    // Fallback if the page never actually goes quiet within this
    // window — still scroll once rather than wait indefinitely.
    hardStop = setTimeout(scrollToCard, 1800);
  });
})();
